import { Grid, Container, Typography } from '@mui/material';
import { ODSCommunities, ODSInformation } from '@telekom-ods/react-icons';
import { Formik, Form } from 'formik';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';
import * as Yup from 'yup';

import Button from '../../components/Buttons/Button';
import { FormikCheckbox, FormikPasswordField } from '../../components/Formik';
import { isValidPassword } from '../../helpers/validation';

interface SetPasswordFormValues {
  password: string;
  passwordConfirm: string;
  termsAccepted: boolean;
}

interface SetPasswordProps {
  onSubmit: (password: string) => void | Promise<void>;
  organizationName: string;
}

const Terms = (): React.JSX.Element => {
  const intl = useIntl();

  return (
    <>
      <Typography gutterBottom>
        {intl.formatMessage({ id: 'termsIntroBeforeLink' })}
        &nbsp;
        <Link to='/gtc'> {intl.formatMessage({ id: 'generalTermsAndConditionsLinkText' })} </Link>
        &nbsp; {intl.formatMessage({ id: 'termsIntroAfterLink' })}
      </Typography>
      <Typography gutterBottom>{intl.formatMessage({ id: 'termsSecondParagraph' })}</Typography>
      <Typography gutterBottom>
        {intl.formatMessage({ id: 'termsThirdParagraphBeforeLink' })}
        &nbsp;
        <Link to='/data-privacy'>{intl.formatMessage({ id: 'privacy' })}</Link>
        &nbsp; {intl.formatMessage({ id: 'termsThirdParagraphAfterLink' })}
      </Typography>
    </>
  );
};

const SetPassword = ({ onSubmit, organizationName }: SetPasswordProps): React.JSX.Element => {
  const intl = useIntl();
  const validationSchema = Yup.object().shape({
    termsAccepted: Yup.boolean().oneOf([true], intl.formatMessage({ id: 'formRequiredError' })),
    password: Yup.string()
      .test('Strong password', intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }), value =>
        value ? isValidPassword(value) : true
      )
      .required(intl.formatMessage({ id: 'formRequiredError' })),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password'), undefined], intl.formatMessage({ id: 'userPasswordMismatch' }))
      .required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const initialValues: SetPasswordFormValues = {
    password: '',
    passwordConfirm: '',
    termsAccepted: false,
  };

  const handleSubmit = (data: SetPasswordFormValues): void | Promise<void> => {
    return onSubmit(data.password);
  };

  return (
    <Container maxWidth='md'>
      <Typography
        variant='h4'
        color='primary'
        gutterBottom
      >
        {intl.formatMessage({ id: 'userActivationTitle' })}
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Grid
              container
              direction='column'
              gap={1}
            >
              <Grid
                item
                display='flex'
              >
                <ODSCommunities />
                <Typography>
                  {intl.formatMessage({ id: 'invitedToOrganization' }, { organizationName })}
                </Typography>
              </Grid>
              <Grid
                item
                display='flex'
              >
                <ODSInformation />
                <Typography>
                  {intl.formatMessage({ id: 'readAboutDtCloudServices' })}&nbsp;
                  <Link to='/about-us'>{intl.formatMessage({ id: 'here' })}</Link>
                </Typography>
              </Grid>
              <Grid
                container
                direction='column'
                width={300}
                py={2}
              >
                <Grid item>
                  <FormikPasswordField
                    label={intl.formatMessage({ id: 'password' })}
                    name='password'
                  />
                </Grid>
                <Grid item>
                  <FormikPasswordField
                    label={intl.formatMessage({ id: 'confirmPassword' })}
                    name='passwordConfirm'
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent='flex-end'
            >
              <Grid item>
                <Terms />
                <FormikCheckbox
                  label={intl.formatMessage({ id: 'accept' })}
                  name='termsAccepted'
                />
              </Grid>
              <Grid item>
                <Button
                  type='submit'
                  name='Submit'
                  disabled={isSubmitting || !isValid || !dirty}
                  label={intl.formatMessage({ id: 'submit' })}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default SetPassword;
